import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TextSanitizerService } from '../../../services/text-sanitizer.service';
import { SearchHighlightField } from '../../../models/search-highlight.model';

/**
 * Text case transformation options for highlighted text.
 */
export type TextTransform = 'none' | 'uppercase' | 'lowercase';

/**
 * Pipe for rendering highlighted search text safely.
 * Single Responsibility: Transforms highlight data into safe HTML for rendering.
 *
 * This pipe follows the Dependency Inversion Principle by depending on
 * TextSanitizerService abstraction for sanitization logic.
 *
 * Usage:
 * - With highlight field: {{ highlightField | highlightText }}
 * - With fallback: {{ highlightField | highlightText:fallbackText }}
 * - With transform: {{ highlightField | highlightText:fallbackText:'uppercase' }}
 * - Snippet only: {{ 'html snippet' | highlightText }}
 *
 * The pipe is SSR-compatible as it doesn't use browser-specific APIs.
 */
@Pipe({
    name: 'highlightText',
})
export class HighlightTextPipe implements PipeTransform {
    constructor(
        private sanitizer: DomSanitizer,
        private textSanitizer: TextSanitizerService,
    ) {}

    /**
     * Transforms highlight data into safe HTML.
     *
     * @param value The highlight field object or raw HTML snippet string
     * @param fallback Optional fallback text to display if no highlight is available
     * @param textTransform Optional text transformation ('none', 'uppercase', 'lowercase')
     * @returns SafeHtml that can be bound with [innerHTML]
     */
    transform(
        value: SearchHighlightField | string | null | undefined,
        fallback?: string,
        textTransform: TextTransform = 'none',
    ): SafeHtml {
        // Handle null/undefined - return fallback or empty
        if (value === null || value === undefined) {
            return this.sanitizeAndTrust(fallback || '', textTransform);
        }

        // Handle string input (raw HTML snippet)
        if (typeof value === 'string') {
            return this.sanitizeAndTrust(value || fallback || '', textTransform);
        }

        // Handle SearchHighlightField object
        const highlightField = value as SearchHighlightField;

        // Use snippet if available, otherwise use value, then fallback
        const textToRender = highlightField.snippet || highlightField.value || fallback || '';

        return this.sanitizeAndTrust(textToRender, textTransform);
    }

    /**
     * Sanitizes HTML content and marks it as trusted for Angular.
     * Uses TextSanitizerService to ensure only safe tags are preserved.
     * @param html The HTML string to sanitize
     * @param textTransform The text transformation to apply
     */
    private sanitizeAndTrust(html: string, textTransform: TextTransform): SafeHtml {
        let processedHtml = html;

        // Apply text transformation while preserving HTML tags
        if (textTransform !== 'none') {
            processedHtml = this.applyTextTransform(html, textTransform);
        }

        const sanitizedHtml = this.textSanitizer.sanitizeHighlightHtml(processedHtml);
        return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
    }

    /**
     * Applies text transformation while preserving HTML tags.
     * Only transforms text content, not tag names or attributes.
     * @param html The HTML string to transform
     * @param transform The transformation to apply
     */
    private applyTextTransform(html: string, transform: TextTransform): string {
        // Split by HTML tags, transform text parts only
        return html.replace(/([^<>]+)(?=<|$)/g, (match) => {
            switch (transform) {
                case 'uppercase':
                    return match.toUpperCase();
                case 'lowercase':
                    return match.toLowerCase();
                default:
                    return match;
            }
        });
    }
}
